import Alert from "/src/util/alert";

import { API_URL_LOGIN, API_URL_PREFIX } from "../config/config";
import { loadState, saveState } from "./redux/localStorage";

const HEADERS = { "Accept": "application/json", "Content-Type": "application/json", "X-Frame-Options": "deny" };

const GENERIC_ERROR = {
  uk: "Щось пішло не так. Спробуйте ще раз.",
  en: "Something went wrong. Please try again.",
  hy: "Ինչ-որ բան սխալ գնաց։ Խնդրում ենք փորձել կրկին։",
  ka: "რაღაც შეცდომა მოხდა. გთხოვთ, სცადოთ თავიდან.",
};

// Safe read of persisted auth state — never throws on missing/corrupt localStorage
// (loadState is try/catch wrapped and returns undefined on failure).
function readState() {
  return loadState() || null;
}

// Benign stand-in handed to a callback when a request fails, so loading states clear
// without crashing the caller: `.id`/`.detail` are undefined (success paths skip) and
// `.results`/`.count` satisfy the many list callers that .map/.length over them.
function failedResponse() {
  return { results: [], count: 0 };
}

function notifyFailure(state) {
  const lang = state?.me?.language || "en";
  Alert.error(GENERIC_ERROR[lang] || GENERIC_ERROR.en);
}

// Expired/invalid token: clear the user and bounce to login. Covers any 401/403, not
// just the exact "Invalid token." body string the app used to match on.
function handleUnauthorized() {
  saveState({ me: null });
  window.location.replace("/login");
}

/** A 400 comes back as the raw `Response` (see `call`) — this reads its body into the field errors
 *  and one joined sentence, best-effort. Lives here because 400 handling is this file's contract:
 *  `"Error: " + JSON.stringify(response)` renders a `Response` as `{}`, which is what every caller
 *  that skipped this showed the user. */
export async function parseErrors(response) {
  const out = { __ok: false, fields: {}, message: "" };
  try {
    const body = await response.json();
    if (body && typeof body === "object") {
      Object.entries(body).forEach(([k, v]) => {
        out.fields[k] = Array.isArray(v) ? v.join(", ") : String(v);
      });
      out.message = Object.values(out.fields).join("; ");
    }
  } catch (e) {
    /* non-JSON body */
  }
  return out;
}

export function call(body = {}, cb, customHeaders = {}, isBlob = false) {
  const headers = new Headers(HEADERS);

  let state = readState();
  headers.append("Authorization", `Token ${state?.me?.key || null}`);

  if (customHeaders) {
    Object.entries(customHeaders).map(header => {
      headers.append(header[0], header[1]);
    });
  }

  let params = { method: body.method, headers, mode: "cors", cache: "default" };

  if (["POST", "PATCH", "PUT"].indexOf(body.method) !== -1) {
    params.body = JSON.stringify(body.post);
  }

  let url = API_URL_PREFIX + body.url;

  if (body.outforz) {
    url = "https://dev.api.outforz.com/api/contents/";
  }

  if (isBlob) {
    fetch(url, params)
      .then(response => {
        if (!response || response.status === 500) {
          notifyFailure(state);
          if (cb) cb();
          return null;
        }
        if (response.status === 401 || response.status === 403) {
          handleUnauthorized();
          return null;
        }
        return response.blob();
      })
      .then(blob => {
        if (!blob) return; // failure already handled above
        let url = window.URL.createObjectURL(blob);
        let a = document.createElement("a");

        a.href = url;
        a.download = "export.zip";
        document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
        a.click();
        a.remove(); //afterwards we remove the element again

        if (cb) cb();
      })
      .catch(err => {
        console.error("Request failed:", err);
        notifyFailure(state);
        if (cb) cb();
      });
  } else {
    fetch(url, params)
      .then(async response => {
        if (!response) {
          notifyFailure(state);
          cb(failedResponse());
          return;
        }
        if (response.status === 401 || response.status === 403) {
          handleUnauthorized();
          return;
        }
        if (response.status === 500) {
          notifyFailure(state);
          cb(failedResponse());
          return;
        }
        // 400/204 callers expect the raw Response (they parse it themselves).
        if (response.status === 400) {
          cb(response);
          return;
        }
        if (response.status === 204) {
          cb(response);
          return;
        }

        let data = null;
        try {
          data = await response.json();
        } catch (e) {
          data = null; // empty / non-JSON body — don't let .json() reject silently
        }
        if (data?.detail === "Invalid token.") {
          handleUnauthorized();
          return;
        }
        cb(data != null ? data : failedResponse());
      })
      .catch(err => {
        console.error("Request failed:", err);
        notifyFailure(state);
        cb(failedResponse());
      });
  }
}

export function httpRequest(body = {}, cb, customHeaders = {}, isBlob = false) {
  const headers = new Headers(HEADERS);

  let state = readState();
  headers.append("Authorization", `Token ${state?.me?.key || null}`);

  if (customHeaders) {
    Object.entries(customHeaders).map(header => {
      headers.append(header[0], header[1]);
    });
  }

  let params = { method: body.method, headers, mode: "cors", cache: "default" };

  if (["POST", "PATCH", "PUT"].indexOf(body.method) !== -1) {
    params.body = JSON.stringify(body.post);
  }

  let url = API_URL_PREFIX + body.url;

  if (body.outforz) {
    url = "https://dev.api.outforz.com/api/contents/";
  }

  if (isBlob) {
    fetch(url, params)
      .then(response => {
        if (!response || response.status === 500) {
          notifyFailure(state);
          if (cb) cb();
          return null;
        }
        if (response.status === 401 || response.status === 403) {
          handleUnauthorized();
          return null;
        }
        return response.blob();
      })
      .then(blob => {
        if (!blob) return;
        let url = window.URL.createObjectURL(blob);
        let a = document.createElement("a");

        a.href = url;
        a.download = "export.zip";
        document.body.appendChild(a);
        a.click();
        a.remove();

        if (cb) cb();
      })
      .catch(err => {
        console.error("Request failed:", err);
        notifyFailure(state);
        if (cb) cb();
      });
  } else {
    fetch(url, params)
      .then(async response => {
        if (!response) {
          notifyFailure(state);
          cb(failedResponse());
          return;
        }
        if (response.status === 401 || response.status === 403) {
          handleUnauthorized();
          return;
        }
        if (response.status === 500) {
          notifyFailure(state);
          cb(failedResponse());
          return;
        }
        if (response.status === 400) {
          cb(response);
          return;
        }
        // httpRequest intentionally hands 200/204 back as the raw Response.
        if (response.status === 204 || response.status === 200) {
          cb(response);
          return;
        }

        let data = null;
        try {
          data = await response.json();
        } catch (e) {
          data = null;
        }
        if (data?.detail === "Invalid token.") {
          handleUnauthorized();
          return;
        }
        cb(data != null ? data : failedResponse());
      })
      .catch(err => {
        console.error("Request failed:", err);
        notifyFailure(state);
        cb(failedResponse());
      });
  }
}

export const callAsync = async (body = {}, cb) => {
  const headers = new Headers(HEADERS);
  let state = readState();

  headers.append("Authorization", `Token ${state?.me?.key || null}`);

  let params = { method: body.method, headers, mode: "cors", cache: "default" };

  if (["POST", "PATCH", "PUT"].indexOf(body.method) !== -1) {
    params.body = JSON.stringify(body.post);
  }

  let url = API_URL_PREFIX + body.url;

  if (body.outforz) {
    url = "https://dev.api.outforz.com/api/contents/";
  }

  fetch(url, params)
    .then(async response => {
      if (!response) {
        notifyFailure(state);
        cb(failedResponse());
        return;
      }
      if (response.status === 401 || response.status === 403) {
        handleUnauthorized();
        return;
      }
      if (response.status === 500) {
        notifyFailure(state);
        cb(failedResponse());
        return;
      }

      let data = null;
      try {
        data = await response.json();
      } catch (e) {
        data = null;
      }
      if (data?.detail === "Invalid token.") {
        handleUnauthorized();
        return;
      }
      cb(data != null ? data : failedResponse());
    })
    .catch(err => {
      console.error("Request failed:", err);
      notifyFailure(state);
      cb(failedResponse());
    });
};

/**
 * Cancellable GET. Returns a promise so a superseded request can be dropped with an
 * AbortController — which the callback-style `call()` has no way to express. Reuses this
 * module's auth header and 401 handling so there is still one place that knows about the token.
 *
 * Rejects with an AbortError when cancelled; callers should ignore that specific name.
 */
export function getAbortable(url, signal) {
  const headers = new Headers(HEADERS);
  const state = readState();
  headers.append("Authorization", `Token ${state?.me?.key || null}`);

  return fetch(API_URL_PREFIX + url, { method: "GET", headers, mode: "cors", signal }).then(response => {
    if (response.status === 401 || response.status === 403) {
      handleUnauthorized();
      return null;
    }
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  });
}

export function auth(body = {}) {
  const headers = new Headers(HEADERS);
  let params = { method: "POST", headers: headers, mode: "cors", cache: "default" };
  params.body = JSON.stringify(body);

  return new Promise((resolve, reject) => {
    fetch(`${API_URL_LOGIN}`, params)
      .then(response => response.json())
      .then(resolve)
      .catch(reject);
  });
}

export function callForm(body = {}, cb) {
  const headers = new Headers();
  let state = readState();
  headers.append("Authorization", `Token ${state?.me?.key || null}`);
  // No Content-Type: the browser sets multipart/form-data; boundary automatically.

  const params = { method: body.method, headers, mode: "cors", cache: "default", body: body.post };
  const url = API_URL_PREFIX + body.url;

  fetch(url, params)
    .then(async response => {
      if (!response) {
        notifyFailure(state);
        cb(failedResponse());
        return;
      }
      if (response.status === 401 || response.status === 403) {
        handleUnauthorized();
        return;
      }
      if (response.status === 500) {
        notifyFailure(state);
        cb(failedResponse());
        return;
      }
      if (response.status === 400) {
        cb(response);
        return;
      }
      if (response.status === 204) {
        cb(response);
        return;
      }

      let data = null;
      try {
        data = await response.json();
      } catch (e) {
        data = null;
      }
      if (data?.detail === "Invalid token.") {
        handleUnauthorized();
        return;
      }
      cb(data != null ? data : failedResponse());
    })
    .catch(err => {
      console.error("Request failed:", err);
      notifyFailure(state);
      cb(failedResponse());
    });
}

export function upload(body = {}, cb) {
  const headers = new Headers();

  let state = readState();
  headers.append("Authorization", `Token ${state?.me?.key || null}`);

  let params = { method: body.method, headers: headers, mode: "cors", cache: "default" };
  params.body = body.post;

  let url = API_URL_PREFIX + body.url;

  fetch(url, params)
    .then(response => {
      if (response.status === 401 || response.status === 403) {
        handleUnauthorized();
        throw new Error("Unauthorized");
      }
      if (!response.ok) {
        // Surface the server error message; fall back to a generic toast if the
        // error body isn't JSON (so a non-JSON 5xx doesn't get swallowed silently).
        return response.json().then(
          errData => {
            Alert.error(String(errData?.error || GENERIC_ERROR[state?.me?.language || "en"] || GENERIC_ERROR.en), { position: "top" });
            throw errData;
          },
          () => {
            notifyFailure(state);
            throw new Error("Upload failed");
          }
        );
      }

      return response.json();
    })
    .then(res => {
      cb(res);
    })
    .catch(err => {
      console.error("Upload failed:", err);
    });
}
